import React, { useEffect } from 'react';

import { Button, Container } from '@mui/material';
import { observer } from 'mobx-react';
import ArrowBackIosRoundedIcon from '@mui/icons-material/ArrowBackIosRounded';

import classes from './NgzChallengeProfile.module.scss';
import { angularizeWithProviders } from '../../../shared/providers';
import {
  LbTabPanel,
  LbTabs,
  LbVerticalPageTabsList,
  TabsConfig,
} from '../../../shared/components/tabs';
import {
  CHALLENGE_PROFILE_TAB,
  ChallengeProfileTab,
} from '../../constants/challengeProfileTab';
import { getRouteParam, setRouteParams } from '../../../shared/utils/angularUtils';
import { useRouteParam } from '../../../shared/hooks/useRouteParam';
import { pathQueryParams } from '../../../shared/constants/pathQueryParams';
import { challengeProfileStore } from '../../stores/challengeProfileStore';
import { ChallengeProfileCard } from './containers/ChallengeProfileCard/ChallengeProfileCard';
import { GeneralTab } from './containers/GeneralTab/GeneralTab';
import { ParticipantsTab } from './containers/ParticipantsTab/ParticipantsTab';
import { SettingsTab } from './containers/SettingsTab/SettingsTab';
import { PromoCodesList } from '../../../payment';
import { SOLUTION_TYPE } from '../../../shared/constants/user-info/solutionType';
import { ChallengePublishedModal } from './components/ChallengePublishedModal/ChallengePublishedModal';

const NgzChallengeProfile = observer(() => {
  const {
    challengeProfileInfo,
    showPublishedModal,
    formVersion,
    getChallengeProfileInfo,
    getChallengeStatusDetails,
    setDefaultValues,
    closePublishedModal,
  } = challengeProfileStore;

  const challengeId = getRouteParam('challengeId');
  const currentTab = useRouteParam(pathQueryParams.currentTab, {
    defaultValue: CHALLENGE_PROFILE_TAB.GENERAL,
  });

  useEffect(() => {
    getChallengeProfileInfo(challengeId);
    getChallengeStatusDetails(challengeId);

    return () => {
      setDefaultValues();
    };
  }, []);

  const tabsConfig: TabsConfig<ChallengeProfileTab>[] = [
    {
      label: 'General',
      value: CHALLENGE_PROFILE_TAB.GENERAL,
      panel: <GeneralTab key={formVersion} challengeId={challengeId} />,
    },
    {
      label: 'Participants',
      value: CHALLENGE_PROFILE_TAB.PARTICIPANTS,
      panel: <ParticipantsTab challengeId={challengeId} />,
    },
    {
      label: 'Promo codes',
      value: CHALLENGE_PROFILE_TAB.PROMO_CODES,
      panel: (
        <PromoCodesList targetId={challengeId} solutionType={SOLUTION_TYPE.CHALLENGE} />
      ),
    },
    {
      label: 'Settings',
      value: CHALLENGE_PROFILE_TAB.SETTINGS,
      panel: <SettingsTab challengeId={challengeId} />,
    },
  ];

  if (!challengeProfileInfo) {
    return null;
  }

  return (
    <Container>
      <Button
        className={classes.backButton}
        variant="text"
        startIcon={<ArrowBackIosRoundedIcon />}
        color="secondary"
        href="#!/challenges-list"
        size="small"
      >
        Back to Challenge List
      </Button>
      <ChallengeProfileCard
        challengeId={challengeId}
        challengeInfo={challengeProfileInfo}
      />
      <LbTabs
        value={currentTab}
        onValueChange={(value) => {
          setRouteParams({ [pathQueryParams.currentTab]: value });
        }}
      >
        <div className={classes.tabsBox}>
          <LbVerticalPageTabsList tabsConfig={tabsConfig} />
          <LbTabPanel tabsConfig={tabsConfig} />
        </div>
      </LbTabs>
      {showPublishedModal && (
        <ChallengePublishedModal
          challengeId={challengeId}
          onClose={closePublishedModal}
        />
      )}
    </Container>
  );
});

angularizeWithProviders(NgzChallengeProfile, 'ngzChallengeProfile', {});

export { NgzChallengeProfile };
